import { useMemo } from "react";

/**
 * Centralized hook for user permissions
 *
 * This hook provides a single source of truth for all permission checks
 * across the application. It reads the user type from localStorage and
 * returns permission flags.
 *
 * For User Type 2, permissions are now module-specific:
 * - If moduleAuthorization is provided and returns {message: false}, user is read-only
 * - If moduleAuthorization is provided and returns {message: true}, user has full access
 * - If no module is selected, default to read-only for User Type 2
 *
 * User Type 1: Full access (can edit, delete, create)
 * User Type 2: Module-specific access based on API authorization
 *
 * @param {Object} options - Configuration options
 * @param {string} options.moduleId - The selected module ID (for User Type 2 authorization check)
 * @param {Object} options.moduleAuthorization - The result from useModuleAuthorizationQuery
 * @returns {Object} Permission flags and user info
 *
 * @example
 * const { data: moduleAuth } = useModuleAuthorizationQuery(selectedModule);
 * const { canEdit, canDelete, isReadOnly } = useUserPermissions({
 *   moduleId: selectedModule,
 *   moduleAuthorization: moduleAuth
 * });
 *
 * {canEdit && <EditButton />}
 * {!isReadOnly && <DeleteOption />}
 */
const useUserPermissions = ({ moduleId, moduleAuthorization } = {}) => {
  const permissions = useMemo(() => {
    try {
      const userDetails = localStorage.getItem("userDetail");
      const user = JSON.parse(userDetails);
      const userType = user?.usertype;

      // For User Type 2, check module-level authorization
      let isReadOnly = false;

      if (userType === 2) {
        // If a module is selected and we have authorization data
        if (moduleId && moduleAuthorization) {
          // API returns {message: false} for unauthorized, {message: true} for authorized
          isReadOnly = moduleAuthorization.message === false;
        } else {
          // No module selected or no auth data yet - default to read-only for safety
          isReadOnly = true;
        }
      }
      // User Type 1 always has full access
      else if (userType === 1) {
        isReadOnly = false;
      }

      return {
        // Core permissions
        canEdit: !isReadOnly, // Can edit test steps, test cases, etc.
        canDelete: !isReadOnly, // Can delete test steps, test cases, etc.
        canCreate: !isReadOnly, // Can create new test steps, test cases, etc.
        canBulkEdit: !isReadOnly, // Can perform bulk operations
        canRename: !isReadOnly, // Can rename test cases
        canConfigure: !isReadOnly, // Can change settings/configurations

        // Specific permissions
        canAddToSuite: !isReadOnly, // Can add test cases to suites
        canSetFlow: !isReadOnly, // Can set flow status
        canSetWaitTime: !isReadOnly, // Can set wait times
        canSetCondLogic: !isReadOnly, // Can set conditional logic
        canInlineSteps: !isReadOnly, // Can create inline dependencies
        canReplaceSteps: !isReadOnly, // Can replace test steps
        canChangeToAssert: !isReadOnly, // Can change steps to assertions

        // General flags
        isReadOnly, // General read-only flag
        userType, // User type number

        // Always allowed (even for read-only users)
        canView: true, // Can view test steps and data
        canExecute: true, // Can execute tests
        canDownload: true, // Can download code/files
        canCopy: true, // Can copy test cases
      };
    } catch (error) {
      console.error("Error reading user permissions:", error);
      // Default to read-only if there's an error
      return {
        canEdit: false,
        canDelete: false,
        canCreate: false,
        canBulkEdit: false,
        canRename: false,
        canConfigure: false,
        canAddToSuite: false,
        canSetFlow: false,
        canSetWaitTime: false,
        canSetCondLogic: false,
        canInlineSteps: false,
        canReplaceSteps: false,
        canChangeToAssert: false,
        isReadOnly: true,
        userType: null,
        canView: true,
        canExecute: true,
        canDownload: true,
        canCopy: true,
      };
    }
  }, [moduleId, moduleAuthorization]); // Re-compute when module or authorization changes

  return permissions;
};

export default useUserPermissions;
